import * as api from '@owl-nest/api-client/next'
import * as logger from '@owl-nest/logger'

export type Comment<IS_ROOT = false> = api.Comment<IS_ROOT> &
  api.WithParam<true, IS_ROOT, { replies_meta?: api.Pageable<api.Comment<IS_ROOT>>['meta'] | null }>

export type CommentList<IS_ROOT = false> = {
  comments: Comment<IS_ROOT>[]
}

export type CommentsParams = {
  limit: number | string
  since: number | string
}

export async function create(
  objectId: number | string,
  payload: api.CreateComment,
): Promise<api.HttpResult<Comment<true>>> {
  const response = await api.client('POST /news/:id/comments', {
    body: payload,
    urlParams: { id: String(objectId) },
  })

  return response.next((response) => response.body)
}

export async function list(
  objectId: number | string,
  getParams?: URLSearchParams,
): Promise<api.HttpResult<api.Pageable<CommentList<true>>>> {
  const response = await api.client('GET /news/:id/comments', {
    getParams,
    urlParams: { id: String(objectId) },
  })

  const limit = getParams?.get('limit') ?? 10
  const since = getParams?.get('since')

  return response
    .next((success) => {
      const comments: Comment<true>[] = success.body.comments.map((comment) => {
        const hasReplies = comment.replies_count > 0 && comment.replies.length > 0
        const lastReply = hasReplies && comment.replies[comment.replies.length - 1]
        const cursor = lastReply ? new Date(lastReply.submit_date).getTime() : Date.now()
        return {
          ...comment,
          replies_meta: hasReplies
            ? {
                limit: Number(limit),
                next: `?limit=${limit}&since=${Math.ceil(cursor / 1000)}`,
                total_count: comment.replies_count,
              }
            : null,
        }
      })
      return {
        ...success.body,
        comments,
      }
    })
    .except((failure) => {
      logger.err('[services/comment] Failed to fetch comments', {
        failure,
        objectId,
        since,
      })
      return {
        comments: [],
        meta: {
          limit: Number(limit),
          next: null,
          total_count: 0,
        },
      }
    })
}

export async function listReplies(
  commentId: number,
  { limit, since }: CommentsParams,
): Promise<api.HttpResult<api.Pageable<CommentList<false>>>> {
  const response = await api.client('GET /comments/:id/replies', {
    getParams: { limit: String(limit), since: String(since) },
    urlParams: { id: String(commentId) },
  })

  return response
    .next((success) => success.body)
    .except((failure) => {
      logger.err('[services/comment] Failed to fetch comments', {
        failure,
        commentId,
        since,
      })
      return {
        comments: [],
        meta: {
          limit: Number(limit),
          next: null,
          total_count: 0,
        },
      }
    })
}

export async function reply(commentId: number, payload: api.CreateComment): Promise<api.HttpResult<Comment<false>>> {
  const response = await api.client('POST /comments/:id/replies', {
    body: payload,
    urlParams: { id: String(commentId) },
  })

  return response.next((response) => response.body)
}
